import { supabase } from '../lib/supabase';
import type { FavoriteVerse } from '../lib/types';

export const favoritesService = {
  async isFavorite(verseId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('favorites')
        .select('id')
        .eq('user_id', user.id)
        .eq('verse_id', verseId)
        .maybeSingle();

      if (error) {
        console.error('Error checking favorite status:', error);
        return false;
      }
      return !!data;
    } catch (error) {
      console.error('Error in isFavorite:', error);
      return false;
    }
  },

  async addFavorite(verse: Omit<FavoriteVerse, 'createdAt'>): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { error } = await supabase
        .from('favorites')
        .upsert({
          user_id: user.id,
          verse_id: verse.id,
          verse_text: verse.text || ''  // Store the verse text
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error adding favorite:', error);
      throw error;
    }
  },

  async removeFavorite(verseId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('verse_id', verseId);

      if (error) throw error;
    } catch (error) {
      console.error('Error removing favorite:', error);
      throw error;
    }
  },

  async getFavorite(verseId: string): Promise<FavoriteVerse | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('favorites')
        .select('*')
        .eq('user_id', user.id)
        .eq('verse_id', verseId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return {
        id: data.verse_id,
        text: data.verse_text || '',  // Get the verse text
        createdAt: data.created_at
      };
    } catch (error) {
      console.error('Error getting favorite:', error);
      return null;
    }
  },

  async getAllFavorites(): Promise<FavoriteVerse[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('favorites')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(favorite => ({
        id: favorite.verse_id,
        text: favorite.verse_text || '',  // Get the verse text
        createdAt: favorite.created_at
      }));
    } catch (error) {
      console.error('Error getting all favorites:', error);
      return [];
    }
  }
};
